import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { pluralizeWithCount } from "@common/helpers/string";
import { useScope } from "@state/scopeContext.lib";
import { useEntities } from "@hooks/useEntities";
import { useSimilarTransactionsNewValue } from "../SimilarTransactionsNewValueContext";
import { useActionsContext } from "./ActionsContext";
import { buildApplyActionsPlan, ApplyActionsPlan } from "./applyActionsPlanner";
import { fetchMatchingTransactionIdsForConditions } from "./rowMatching";

interface UseApplyActionsResult {
    applyActions: () => void;
    isApplying: boolean;
    isButtonVisible: boolean;
    isButtonDisabled: boolean;
    tooltip: string;
}

const EMPTY_PLAN: ApplyActionsPlan = {
    totalMatchedTransactions: 0,
    unsyncedTransactionsCount: 0,
    assignments: [],
};

export function useApplyActions(): UseApplyActionsResult {
    const { workspaceId } = useScope();
    const {
        rows,
        matchingScopeConditions,
        rowIdToOwnedTxIds,
        valueToAssign,
        filters,
    } = useActionsContext();
    const { newValuesMap, setNewValues } = useSimilarTransactionsNewValue();
    const entities = useEntities({ excludeMockEntities: true });
    const [hasEverShownButton, setHasEverShownButton] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const rowOwnershipEntries = useMemo(
        () => Array.from(rowIdToOwnedTxIds.entries()),
        [rowIdToOwnedTxIds],
    );
    const planQueryKey = useMemo(
        () => [
            "similar-tx-apply-actions-plan",
            workspaceId,
            rows,
            matchingScopeConditions,
            rowOwnershipEntries,
            valueToAssign,
            newValuesMap,
            filters.excludedTransactionIds,
            entities,
        ],
        [
            workspaceId,
            rows,
            matchingScopeConditions,
            rowOwnershipEntries,
            valueToAssign,
            newValuesMap,
            filters.excludedTransactionIds,
            entities,
        ],
    );

    const hasConditionBasedAction = useMemo(
        () =>
            rows.some(
                (row) =>
                    row.conditions.length > 0 && row.action.value !== undefined,
            ),
        [rows],
    );

    const planQuery = useQuery({
        queryKey: planQueryKey,
        queryFn: async () => {
            if (!workspaceId) {
                return EMPTY_PLAN;
            }

            return await buildApplyActionsPlan({
                rows,
                scopeConditions: matchingScopeConditions,
                rowIdToOwnedTxIds,
                valueToAssign,
                newValuesMap,
                entities,
                fetchMatchingTransactionIdsForConditions: async (conditions) =>
                    await fetchMatchingTransactionIdsForConditions({
                        workspaceId,
                        conditions,
                        excludedTransactionIds: filters.excludedTransactionIds,
                    }),
            });
        },
        enabled: !!workspaceId && hasConditionBasedAction,
        staleTime: 10_000,
    });

    const plan = planQuery.data ?? EMPTY_PLAN;

    useEffect(() => {
        if (plan.unsyncedTransactionsCount > 0) {
            setHasEverShownButton(true);
        }
    }, [plan.unsyncedTransactionsCount]);

    const applyActions = useCallback(() => {
        if (isApplying || plan.assignments.length === 0) {
            return;
        }

        setIsApplying(true);
        setNewValues(plan.assignments);
        setIsApplying(false);
    }, [isApplying, plan.assignments, setNewValues]);

    const isButtonDisabled =
        isApplying ||
        planQuery.isFetching ||
        plan.unsyncedTransactionsCount === 0;
    let tooltip =
        "All condition-based actions are already applied to the transactions list.";
    if (plan.unsyncedTransactionsCount > 0) {
        tooltip = `Kick will check actions from top to bottom and update ${pluralizeWithCount(
            "transaction",
            plan.unsyncedTransactionsCount,
        )} with the first matching action.`;
    }
    if (planQuery.isFetching) {
        tooltip = "Kick is checking which transactions match these actions.";
    }

    return {
        applyActions,
        isApplying,
        isButtonVisible: hasEverShownButton,
        isButtonDisabled,
        tooltip,
    };
}
